/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { v4 } from 'uuid';
import Highcharts from 'highcharts';
import xrange from 'highcharts/modules/xrange';
import HighchartsReact from 'highcharts-react-official';

import { DATE_DAILY_FORMAT } from '../../constants/date';
import { DEFAULT_GROUP } from '../../constants/common';
import { getHexString } from '../../utils/colors';

xrange(Highcharts);

const hasLabels = (preparedLabels) => {
  const filteredLabels = preparedLabels.filter((label) => !label.hidden);
  return !!filteredLabels.length;
};

const prepareLabels = (labels, zoneId, from, to) => [
  { hidden: true, x: from, x2: from },
  { hidden: true, x: to, x2: to },

  ...labels
    .map((label) => ({
      ...label,
      x: moment.tz(label.duration[0], DATE_DAILY_FORMAT, zoneId).startOf('day').valueOf(),
      x2: moment
        .tz(label.duration[1], DATE_DAILY_FORMAT, zoneId)
        .add(1, 'day')
        .startOf('day')
        .valueOf(),
      y: 0,
    }))
    .filter((label) => label.x <= to && label.x2 > from)
    .map((label) => ({
      ...label,
      x: Math.max(label.x, from),
      x2: Math.min(label.x2, to),
    })),
];

const getDurationText = ([lbText, ubText]) =>
  lbText === ubText ? lbText : `${lbText} - ${ubText}`;

const calculateChartHeight = (points) => {
  const maxY = Math.max(...points.map((o) => o.y));
  return maxY * 25 + 100;
};

const setPointsVerticalPositions = (points, position = 0.5) => {
  const getMinCoord = ({ shapeArgs, dataLabel }) => Math.min(shapeArgs.x, dataLabel.alignAttr.x);
  const getMaxCoord = ({ shapeArgs, dataLabel }) =>
    Math.max(shapeArgs.x + shapeArgs.width, dataLabel.alignAttr.x + dataLabel.width);
  const [first, ...rest] = points;
  const intersectingPoints = [];
  let end = getMaxCoord(first);

  first.update({ y: position });

  if (rest.length) {
    rest.forEach((point) => {
      if (end <= getMinCoord(point)) {
        point.update({ y: position });
        end = getMaxCoord(point);
      } else {
        intersectingPoints.push(point);
      }
    });

    if (intersectingPoints.length) {
      setPointsVerticalPositions(intersectingPoints, position + 1);
    }
  }
};

const getLabelsPoints = (series) => {
  const labels = series.find((i) => i?.name === 'Labels');
  const pointsArr = labels?.points;
  return [...pointsArr].sort((a, b) => a.x - b.x);
};

const checkIsDashboardAndFilterItemsById = (fromDashboard, chart, unitId) => {
  // если на дашбордах, то фильтруем графики и отображаем Plot bands на отфильтрованных графиках айтема
  // иначе отображаем Plot bands на всех графиках вкладки
  return fromDashboard ? chart.userOptions.unitId === unitId : true;
};

const prepareChartConfig = (labels, userZoneId, fromDashboard, unitId) => ({
  time: {
    getTimezoneOffset: (timestamp) => -moment.tz(timestamp, userZoneId).utcOffset(),
  },
  groups: ['zoomSync', DEFAULT_GROUP],
  chart: {
    animation: false,
    type: 'xrange',
    marginLeft: 50,
    spacingLeft: 0,
    events: {
      load() {
        const points = getLabelsPoints(this.series);
        setPointsVerticalPositions(points);
        this.setSize(undefined, calculateChartHeight(points));
        // изменяем размер с учётом наличия/отсутствия скрола
        this.setSize(undefined, undefined);
      },
    },
  },
  credits: {
    enabled: false,
  },
  title: {
    text: '',
  },
  legend: {
    enabled: false,
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 1,
    labels: {
      enabled: false,
    },
    minRange: -1,
  },
  yAxis: {
    visible: false,
  },
  tooltip: {
    formatter() {
      return `
           Title: <b>${this.point.title}</b><br>
           ${this.point.description ? `Description: <b>${this.point.description}</b><br>` : ''}
           Duration: <b>${getDurationText(this.point.duration)}</b>
         `;
    },
  },
  plotOptions: {
    xrange: {
      dataLabels: {
        color: 'rgb(105, 128, 154)',
        enabled: true,
        format: '{point.title}',
        allowOverlap: true,
        y: -13,
        style: {
          textOverflow: 'ellipsis',
          width: '200px',
        },
      },
    },
  },
  exporting: {
    buttons: {
      contextButton: {
        enabled: false,
      },
    },
  },
  series: [
    {
      name: 'Labels',
      borderColor: 'none',
      borderRadius: 0,
      maxPointWidth: 5,
      tooltip: {
        followPointer: true,
      },
      data: labels
        .filter((l) => !l.hidden)
        .map((l, i) => ({ ...l, color: getHexString(l.color), y: i })),
      point: {
        events: {
          mouseOver(e) {
            Highcharts.charts
              .filter((c) => !!c && checkIsDashboardAndFilterItemsById(fromDashboard, c, unitId))
              .forEach((chart) => {
                chart.xAxis[0].addPlotBand({
                  from: e.target.x,
                  to: e.target.x2,
                  color: 'rgba(205, 217, 224, .5)',
                  id: 'plot-band',
                });
              });
          },
          mouseOut: () => {
            Highcharts.charts
              .filter((c) => !!c)
              .forEach((chart) => {
                chart.xAxis[0].removePlotBand('plot-band');
              });
          },
        },
      },
    },
    {
      data: labels.filter((l) => !!l.hidden).map(({ x, x2 }) => ({ x, x2, y: 0 })),
    },
  ],
});

const LabelsChart = ({
  labels,
  zoneId,
  userZoneId,
  from,
  to,
  show = true,
  unitId,
  fromDashboard,
}) => {
  const config = useMemo(() => {
    const preparedLabels = prepareLabels(labels, zoneId, from, to);
    if (hasLabels(preparedLabels)) {
      return prepareChartConfig(preparedLabels, userZoneId, fromDashboard, unitId);
    }
    return undefined;
  }, [from, to]);

  const key = useMemo(() => v4(), [config]);

  return (
    show &&
    config && (
      <HighchartsReact
        highcharts={Highcharts}
        options={config}
        isPureConfig
        key={key}
        callback={(c) => {
          const cVar = c;

          if (unitId) {
            cVar.userOptions.unitId = unitId;
          }
        }}
      />
    )
  );
};

export default LabelsChart;
